import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Save, Settings, Percent, Tag, CreditCard, Plus, Trash2, FolderOpen } from "lucide-react";
import api from "../lib/api";

const CHANNELS = [
  { value: "Retail", label: "Retail / Normal Trade" },
  { value: "GDO", label: "GDO" },
  { value: "DO", label: "DO" },
  { value: "Ho.Re.Ca.", label: "Ho.Re.Ca." },
  { value: "Distributore", label: "Distributore" },
];

export default function SettingsPage() {
  const [rates, setRates] = useState({
    Retail: 5,
    GDO: 3,
    DO: 4,
    "Ho.Re.Ca.": 6,
    Distributore: 2,
  });
  const [discounts, setDiscounts] = useState({
    Retail: 0,
    GDO: 5,
    DO: 3,
    "Ho.Re.Ca.": 8,
    Distributore: 10,
  });
  const [loading, setLoading] = useState(true);
  const [savingRates, setSavingRates] = useState(false);
  const [savingDiscounts, setSavingDiscounts] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [savingPayments, setSavingPayments] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    name: "",
    days: 0,
    description: ""
  });
  const [digitalMaterials, setDigitalMaterials] = useState({
    drive_url: "",
    drive_description: ""
  });
  const [savingDigital, setSavingDigital] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [savingNotification, setSavingNotification] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [ratesRes, discountsRes, paymentsRes, digitalRes, notificationRes] = await Promise.all([
        api.get("/settings/commission-rates"),
        api.get("/settings/channel-discounts"),
        api.get("/settings/payment-methods"),
        api.get("/settings/digital-materials"),
        api.get("/settings/notification-email"),
      ]);
      
      const ratesObj = {};
      ratesRes.data.rates.forEach((r) => {
        ratesObj[r.channel] = r.rate * 100;
      });
      setRates((prev) => ({ ...prev, ...ratesObj }));
      
      const discountsObj = {};
      discountsRes.data.discounts.forEach((d) => {
        discountsObj[d.channel] = d.discount;
      });
      setDiscounts((prev) => ({ ...prev, ...discountsObj }));
      
      setPaymentMethods(paymentsRes.data.methods || []);
      
      setDigitalMaterials({
        drive_url: digitalRes.data.drive_url || "",
        drive_description: digitalRes.data.drive_description || ""
      });
      
      setNotificationEmail(notificationRes.data.email || "");

      setPaymentMethods(paymentsRes.data.methods || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      const ratesArray = CHANNELS.map((channel) => ({
        channel: channel.value,
        rate: rates[channel.value] / 100,
      }));
      await api.put("/settings/commission-rates", ratesArray);
      toast.success("Aliquote provvigioni aggiornate con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio delle aliquote");
    } finally {
      setSavingRates(false);
    }
  };

  const handleSaveDiscounts = async () => {
    setSavingDiscounts(true);
    try {
      const discountsArray = CHANNELS.map((channel) => ({
        channel: channel.value,
        discount: discounts[channel.value],
      }));
      await api.put("/settings/channel-discounts", discountsArray);
      toast.success("Sconti commerciali aggiornati con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio degli sconti");
    } finally {
      setSavingDiscounts(false);
    }
  };

  const handleRateChange = (channel, value) => {
    const numValue = parseFloat(value) || 0;
    setRates((prev) => ({
      ...prev,
      [channel]: Math.min(100, Math.max(0, numValue)),
    }));
  };

  const handleDiscountChange = (channel, value) => {
    const numValue = parseFloat(value) || 0;
    setDiscounts((prev) => ({
      ...prev,
      [channel]: Math.min(100, Math.max(0, numValue)),
    }));
  };

  const handleAddPaymentMethod = async () => {
    if (!newPaymentMethod.name.trim()) {
      toast.error("Inserisci il nome della modalità di pagamento");
      return;
    }
    
    setSavingPayments(true);
    try {
      await api.post("/settings/payment-methods", newPaymentMethod);
      toast.success("Modalità di pagamento aggiunta");
      setNewPaymentMethod({ name: "", days: 0, description: "" });
      fetchSettings();
    } catch (error) {
      toast.error("Errore nell'aggiunta della modalità di pagamento");
    } finally {
      setSavingPayments(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    try {
      await api.delete(`/settings/payment-methods/${methodId}`);
      toast.success("Modalità di pagamento eliminata");
      fetchSettings();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSaveDigitalMaterials = async () => {
    setSavingDigital(true);
    try {
      await api.put(`/settings/digital-materials?drive_url=${encodeURIComponent(digitalMaterials.drive_url)}&drive_description=${encodeURIComponent(digitalMaterials.drive_description)}`);
      toast.success("Impostazioni materiale digitale salvate");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingDigital(false);
    }
  };

  const handleSaveNotificationEmail = async () => {
    setSavingNotification(true);
    try {
      await api.put(`/settings/notification-email?email=${encodeURIComponent(notificationEmail)}`);
      toast.success("Email notifiche salvata");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingNotification(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 w-48 bg-muted rounded" />
        <div className="h-64 bg-muted rounded-xl" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold flex items-center gap-3">
            <Settings className="w-8 h-8 text-accent" />
            Impostazioni
          </h1>
          <p className="text-muted-foreground">
            Configura le impostazioni del sistema CRM
          </p>
        </div>
      </div>

      {/* Channel Discounts Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-green-500" />
            Sconti Commerciali per Canale
          </CardTitle>
          <CardDescription>
            Definisci lo sconto commerciale base per ogni canale di vendita.
            Lo sconto viene applicato sul Listino Commerciale del prodotto.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CHANNELS.map((channel) => (
              <div key={channel.value} className="space-y-3">
                <Label htmlFor={`discount-${channel.value}`} className="text-base font-medium">
                  {channel.label}
                </Label>
                <div className="relative">
                  <Input
                    id={`discount-${channel.value}`}
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={discounts[channel.value]}
                    onChange={(e) => handleDiscountChange(channel.value, e.target.value)}
                    className="pr-10 text-lg font-mono"
                    data-testid={`discount-${channel.value.toLowerCase().replace(".", "")}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                    %
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(discounts[channel.value] * 2.5, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                <p>Lo sconto canale è il primo sconto applicato al listino commerciale.</p>
                <p>Successivamente vengono applicati gli sconti personalizzati del cliente.</p>
              </div>
              <Button
                onClick={handleSaveDiscounts}
                className="bg-green-600 hover:bg-green-700"
                disabled={savingDiscounts}
                data-testid="save-discounts-btn"
              >
                {savingDiscounts ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Salvataggio...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Salva Sconti
                  </span>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-500" />
            Modalità di Pagamento
          </CardTitle>
          <CardDescription>
            Definisci le modalità di pagamento disponibili per i clienti.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Existing Payment Methods */}
          <div className="space-y-3">
            {paymentMethods.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nessuna modalità di pagamento configurata
              </p>
            ) : (
              paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-base">{method.name}</span>
                      {method.days > 0 && (
                        <span className="text-sm text-muted-foreground">
                          ({method.days} giorni)
                        </span>
                      )}
                    </div>
                    {method.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {method.description}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeletePaymentMethod(method.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Add New Payment Method */}
          <div className="pt-6 border-t border-border">
            <h3 className="text-sm font-medium mb-4">Aggiungi Nuova Modalità</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment-name">Nome *</Label>
                <Input
                  id="payment-name"
                  placeholder="es. Bonifico Bancario"
                  value={newPaymentMethod.name}
                  onChange={(e) =>
                    setNewPaymentMethod({ ...newPaymentMethod, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-days">Giorni</Label>
                <Input
                  id="payment-days"
                  type="number"
                  min="0"
                  placeholder="es. 30"
                  value={newPaymentMethod.days}
                  onChange={(e) =>
                    setNewPaymentMethod({ ...newPaymentMethod, days: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-desc">Descrizione</Label>
                <Input
                  id="payment-desc"
                  placeholder="es. Pagamento a 30 giorni"
                  value={newPaymentMethod.description}
                  onChange={(e) =>
                    setNewPaymentMethod({ ...newPaymentMethod, description: e.target.value })
                  }
                />
              </div>
            </div>
            <Button
              onClick={handleAddPaymentMethod}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
              disabled={savingPayments}
            >
              {savingPayments ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Aggiunta...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Aggiungi Modalità
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commission Rates Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-accent" />
            Aliquote Provvigioni Agenti
          </CardTitle>
          <CardDescription>
            Definisci la percentuale di provvigione per ogni canale di vendita.
            Le provvigioni vengono calcolate automaticamente sugli ordini.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CHANNELS.map((channel) => (
              <div key={channel.value} className="space-y-3">
                <Label htmlFor={`rate-${channel.value}`} className="text-base font-medium">
                  {channel.label}
                </Label>
                <div className="relative">
                  <Input
                    id={`rate-${channel.value}`}
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={rates[channel.value]}
                    onChange={(e) => handleRateChange(channel.value, e.target.value)}
                    className="pr-10 text-lg font-mono"
                    data-testid={`rate-${channel.value.toLowerCase().replace(".", "")}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                    %
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(rates[channel.value] * 5, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                <p>Le modifiche saranno applicate ai nuovi ordini.</p>
                <p>Gli ordini esistenti manterranno le provvigioni originali.</p>
              </div>
              <Button
                onClick={handleSaveRates}
                className="bg-accent hover:bg-accent/90"
                disabled={savingRates}
                data-testid="save-rates-btn"
              >
                {savingRates ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Salvataggio...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Salva Aliquote
                  </span>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Riepilogo Sconti Canale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {CHANNELS.map((channel) => (
                <div
                  key={channel.value}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <span className="font-medium">{channel.label}</span>
                  <span className="font-mono text-lg text-green-600">
                    -{discounts[channel.value]}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Riepilogo Provvigioni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {CHANNELS.map((channel) => (
                <div
                  key={channel.value}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <span className="font-medium">{channel.label}</span>
                  <span className="font-mono text-lg text-accent">
                    {rates[channel.value]}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Digital Materials Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-accent" />
            Materiale Digitale
          </CardTitle>
          <CardDescription>
            Configura il link all'archivio documenti esterno (Google Drive, Dropbox, ecc.)
            che sarà visibile a tutti gli utenti nella sezione "Materiale Digitale".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="drive-url">URL Archivio Documenti</Label>
            <Input
              id="drive-url"
              type="url"
              placeholder="https://drive.google.com/drive/folders/..."
              value={digitalMaterials.drive_url}
              onChange={(e) =>
                setDigitalMaterials({ ...digitalMaterials, drive_url: e.target.value })
              }
            />
            <p className="text-xs text-muted-foreground">
              Inserisci il link completo alla cartella condivisa (Google Drive, Dropbox, OneDrive, ecc.)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="drive-description">Descrizione</Label>
            <Textarea
              id="drive-description"
              placeholder="Accedi all'archivio completo con cataloghi, listini e materiale marketing..."
              rows={2}
              value={digitalMaterials.drive_description}
              onChange={(e) =>
                setDigitalMaterials({ ...digitalMaterials, drive_description: e.target.value })
              }
            />
          </div>
          <div className="pt-4 border-t border-border">
            <Button
              onClick={handleSaveDigitalMaterials}
              className="bg-accent hover:bg-accent/90"
              disabled={savingDigital}
            >
              {savingDigital ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Salvataggio...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Salva Impostazioni
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-accent" />
            Email Notifiche Admin
          </CardTitle>
          <CardDescription>
            Indirizzo email dove ricevere le notifiche di nuove registrazioni (clienti e agenti)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="notification-email">Email per Notifiche</Label>
            <Input
              id="notification-email"
              type="email"
              placeholder="admin@tuaazienda.com"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Riceverai qui le notifiche quando un nuovo cliente o agente si registra
            </p>
          </div>
          <div className="pt-4 border-t border-border">
            <Button
              onClick={handleSaveNotificationEmail}
              className="bg-accent hover:bg-accent/90"
              disabled={savingNotification}
            >
              {savingNotification ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Salvataggio...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Salva Email
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
